import Reveal from './Reveal';
import { Smartphone, Zap, Bell, CheckCircle2, Clock, Lock, RefreshCw, Share, ChevronLeft, ChevronRight } from 'lucide-react';

const MobileSection = () => {
    const cards = [
        {
            icon: Smartphone,
            title: 'Responsive Interface',
            desc: 'Touch-optimized UI that adapts perfectly to any screen size. View request details, inspect responses, and analyze results with ease on mobile devices.',
            color: 'from-cyan-500 to-blue-500',
            borderColor: 'border-cyan-500/20',
            iconBg: 'bg-cyan-500/10',
            iconColor: 'text-cyan-400',
        },
        {
            icon: Zap,
            title: 'Test on the Go',
            desc: 'Debug production issues while commuting, run quick tests during meetings, or verify deployments from your phone. Full testing power in your pocket.',
            color: 'from-pink-500 to-rose-500',
            borderColor: 'border-pink-500/20',
            iconBg: 'bg-pink-500/10',
            iconColor: 'text-pink-400',
        },
        {
            icon: Bell,
            title: 'Real-time Notifications',
            desc: 'Get instant alerts when scheduled tests fail or when load tests complete. Stay informed about your API health wherever you are.',
            color: 'from-emerald-500 to-green-500',
            borderColor: 'border-emerald-500/20',
            iconBg: 'bg-emerald-500/10',
            iconColor: 'text-emerald-400',
        },
    ];

    return (
        <section className="py-20 sm:py-28 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-cyan-500/5 blur-[120px] rounded-full" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="text-center mb-16 sm:mb-20">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-mono mb-6 sm:mb-8">
                        <Smartphone className="w-3.5 h-3.5" />
                        Mobile-First Design
                    </div>
                    <Reveal>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-6 sm:mb-8">
                            Test APIs{' '}
                            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Anywhere, Anytime</span>
                        </h2>
                        <p className="text-base sm:text-lg text-gray-500 leading-relaxed max-w-3xl mx-auto">
                            Fully responsive platform that works seamlessly on your phone, tablet, or desktop. Run API tests on the go, get instant notifications, and monitor your services from anywhere.
                        </p>
                    </Reveal>
                </div>

                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                    {/* Feature cards */}
                    <div className="flex-1 space-y-4 sm:space-y-5 w-full max-w-xl order-2 lg:order-1">
                        {cards.map((card, i) => (
                            <Reveal key={i} delay={i * 100}>
                                <div className={`bg-[#111111] rounded-xl p-5 sm:p-7 border border-white/5 hover:${card.borderColor} transition-all duration-500 group`}>
                                    <div className="flex items-start gap-4 sm:gap-5">
                                        <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg ${card.iconBg} border ${card.borderColor} flex items-center justify-center ${card.iconColor} flex-shrink-0 group-hover:scale-110 transition-transform`}>
                                            <card.icon className="w-6 h-6 sm:w-7 sm:h-7" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg sm:text-xl font-bold text-white mb-2 font-mono">{card.title}</h3>
                                            <p className="text-gray-500 leading-relaxed text-sm">{card.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    {/* Phone mockup */}
                    <div className="flex-1 relative flex justify-center order-1 lg:order-2">
                        <div className="relative">
                            <div className="iphone-frame relative w-[260px] h-[530px] sm:w-[290px] sm:h-[590px] rounded-[48px] sm:rounded-[54px] bg-gradient-to-br from-[#2a2a2c] via-[#1c1c1e] to-[#2c2c2e] p-[3px] shadow-[0_50px_100px_-20px_rgba(0,240,255,0.1),0_20px_50px_-10px_rgba(0,0,0,0.5)]">
                                <div className="absolute -left-[2.5px] top-[18%] w-[2.5px] h-[16px] bg-gradient-to-b from-[#3a3a3c] to-[#2c2c2e] rounded-l-[1px]"></div>
                                <div className="absolute -left-[2.5px] top-[24%] w-[2.5px] h-[32px] bg-gradient-to-b from-[#3a3a3c] to-[#2c2c2e] rounded-l-[1px]"></div>
                                <div className="absolute -left-[2.5px] top-[33%] w-[2.5px] h-[32px] bg-gradient-to-b from-[#3a3a3c] to-[#2c2c2e] rounded-l-[1px]"></div>
                                <div className="absolute -right-[2.5px] top-[28%] w-[2.5px] h-[48px] bg-gradient-to-b from-[#3a3a3c] to-[#2c2c2e] rounded-r-[1px]"></div>

                                <div className="w-full h-full rounded-[45px] sm:rounded-[51px] bg-[#0a0a0a] p-[5px] sm:p-[6px]">
                                    <div className="w-full h-full rounded-[40px] sm:rounded-[45px] bg-[#0a0a0a] overflow-hidden relative flex flex-col border border-white/5">
                                        {/* Status bar */}
                                        <div className="flex items-center px-4 sm:px-5 pt-[10px] sm:pt-[12px] pb-[4px]">
                                            <div className="flex-1 flex items-center">
                                                <span className="text-label sm:text-xs font-semibold text-gray-400 font-mono">9:41</span>
                                            </div>
                                            <div className="w-[80px] sm:w-[86px] h-[24px] sm:h-[26px] bg-[#1a1a1a] rounded-[14px] sm:rounded-[15px] flex items-center px-[10px] flex-shrink-0">
                                                <div className="w-[7px] h-[7px] rounded-full bg-[#0c0c14] ring-[0.5px] ring-[#1a1a2e]"></div>
                                            </div>
                                            <div className="flex-1 flex items-center gap-[3px] sm:gap-[4px] justify-end">
                                                <span className="text-label sm:text-label font-bold text-gray-500 font-mono">5G</span>
                                            </div>
                                        </div>

                                        {/* URL bar */}
                                        <div className="mt-1 mx-3 sm:mx-4">
                                            <div className="h-[34px] sm:h-[36px] bg-[#1a1a1a] rounded-[10px] flex items-center px-3 gap-2 border border-white/5">
                                                <Lock className="w-[10px] h-[10px] text-cyan-500/50 flex-shrink-0" />
                                                <span className="text-label sm:text-xs text-gray-500 font-mono truncate flex-1">imago.spense.tech/api</span>
                                                <RefreshCw className="w-[11px] h-[11px] text-gray-600 flex-shrink-0" />
                                            </div>
                                        </div>

                                        {/* Content area — real app screen (API request + 200 response) */}
                                        <div className="flex-1 bg-[#0a0a0a] relative overflow-hidden mt-2">
                                            <img
                                                src="/mobile-api-screen.svg"
                                                alt="Imago running an API request on mobile, with a 200 response"
                                                className="absolute inset-0 w-full h-full object-cover object-top"
                                                loading="lazy"
                                            />
                                        </div>

                                        {/* Bottom bar */}
                                        <div className="px-6 sm:px-8 py-2.5 sm:py-3 bg-[#0d0d0d] flex items-center justify-between border-t border-white/5">
                                            <ChevronLeft className="w-[18px] h-[18px] text-cyan-500/50" />
                                            <ChevronRight className="w-[18px] h-[18px] text-gray-700" />
                                            <Share className="w-[17px] h-[17px] text-cyan-500/50" />
                                        </div>

                                        <div className="flex justify-center pb-[7px] sm:pb-[9px] pt-[2px] bg-[#0d0d0d]">
                                            <div className="w-[100px] sm:w-[120px] h-[4px] sm:h-[5px] bg-gray-600 rounded-full"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating badges */}
                            <div className="hidden lg:flex absolute top-[25%] -right-20 xl:-right-28 bg-[#111111]/95 backdrop-blur-md rounded-xl p-4 shadow-[0_8px_30px_rgba(0,0,0,0.3)] border border-cyan-500/10 items-center gap-3 animate-float-right z-20">
                                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                                    <Clock className="w-5 h-5" />
                                </div>
                                <div>
                                    <h5 className="font-mono font-bold text-white text-xs">Response Time</h5>
                                    <p className="text-label text-cyan-400/60 font-mono">124ms avg</p>
                                </div>
                            </div>

                            <div className="hidden lg:flex absolute top-[58%] -left-20 xl:-left-28 bg-[#111111]/95 backdrop-blur-md rounded-xl p-4 shadow-[0_8px_30px_rgba(0,0,0,0.3)] border border-emerald-500/10 items-center gap-3 animate-float-left z-20">
                                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                                    <CheckCircle2 className="w-5 h-5" />
                                </div>
                                <div>
                                    <h5 className="font-mono font-bold text-white text-xs">Tests Passed</h5>
                                    <p className="text-label text-emerald-400/60 font-mono">41/41 verified</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes float-right {
                    0%, 100% { transform: translateY(0) translateX(0); }
                    50% { transform: translateY(-10px) translateX(4px); }
                }
                @keyframes float-left {
                    0%, 100% { transform: translateY(0) translateX(0); }
                    50% { transform: translateY(-10px) translateX(-4px); }
                }
                .animate-float-right {
                    animation: float-right 5s ease-in-out infinite;
                }
                .animate-float-left {
                    animation: float-left 5s ease-in-out infinite;
                    animation-delay: -2.5s;
                }
            `}</style>
        </section>
    );
};

export default MobileSection;
